import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiFileUploader } from 'omnium-ui/file-uploader';
import { OuiFileItem } from 'omnium-ui/file-item';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTooltipModule } from 'omnium-ui/tooltip';
import {
  ActionAuthorizationService,
  doublonsBusterActionCodes
} from '../../../../core/auth/action-authorization.service';
import {
  AprilAdiExecutionItem,
  AprilAdiIntegrationResult
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';

@Component({
  selector: 'dbw-april-adi-integration-page',
  imports: [
    CommonModule,
    OuiButtonModule,
    OuiFileUploader,
    OuiFileItem,
    OuiIconModule,
    OuiTooltipModule,
    StateCardComponent
  ],
  templateUrl: './april-adi-integration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./april-adi-integration.component.scss']
})
export class AprilAdiIntegrationPageComponent {
  private static readonly HISTORY_PAGE_SIZE = 10;

  readonly file = signal<File | null>(null);
  readonly simulating = signal(false);
  readonly executing = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly result = signal<AprilAdiIntegrationResult | undefined>(undefined);
  readonly awaitingConfirmation = signal(false);
  readonly downloadingReportId = signal<number | null>(null);

  readonly historyItems = signal<AprilAdiExecutionItem[]>([]);
  readonly historyPageIndex = signal(0);
  readonly historyTotalCount = signal(0);
  readonly loadingHistory = signal(false);

  private readonly simulatedFileKey = signal<string | null>(null);

  readonly authorized = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.aprilAdiIntegrationExecute)
  );

  readonly busy = computed(() => this.simulating() || this.executing());

  readonly canSimulate = computed(() => this.authorized() && !!this.file() && !this.busy());

  readonly canExecute = computed(
    () =>
      this.authorized() &&
      !!this.file() &&
      !this.busy() &&
      this.simulatedFileKey() === this.fileKey(this.file())
  );

  readonly historyHasPrevious = computed(() => this.historyPageIndex() > 0);
  readonly historyHasNext = computed(
    () =>
      (this.historyPageIndex() + 1) * AprilAdiIntegrationPageComponent.HISTORY_PAGE_SIZE <
      this.historyTotalCount()
  );

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService
  ) {
    this.loadHistory(0);
  }

  onFilesChange(files: File[] | null): void {
    this.file.set(files?.[0] ?? null);
    this.result.set(undefined);
    this.error.set(undefined);
    this.awaitingConfirmation.set(false);
  }

  removeFile(): void {
    this.onFilesChange(null);
  }

  simulate(): void {
    const file = this.file();

    if (!file || this.busy()) {
      return;
    }

    this.simulating.set(true);
    this.error.set(undefined);
    this.result.set(undefined);
    this.awaitingConfirmation.set(false);

    this.apiService
      .simulateAprilAdiIntegration(file)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.simulating.set(false))
      )
      .subscribe({
        next: result => {
          this.result.set(result);
          this.simulatedFileKey.set(this.fileKey(file));
          this.snackbarService.open('La simulation est terminée.', 'success');
          this.loadHistory(0);
        },
        error: httpError => {
          this.error.set(httpError?.error?.detail ?? 'La simulation a échoué.');
          this.snackbarService.open('La simulation a échoué.', 'error');
        }
      });
  }

  requestExecution(): void {
    if (this.canExecute()) {
      this.awaitingConfirmation.set(true);
    }
  }

  cancelExecution(): void {
    this.awaitingConfirmation.set(false);
  }

  confirmExecution(): void {
    const file = this.file();

    if (!file || this.busy()) {
      return;
    }

    this.executing.set(true);
    this.error.set(undefined);
    this.awaitingConfirmation.set(false);

    this.apiService
      .executeAprilAdiIntegration(file)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.executing.set(false))
      )
      .subscribe({
        next: result => {
          this.result.set(result);
          this.simulatedFileKey.set(null);
          this.snackbarService.open(
            result.rolledBack
              ? "L'intégration a été rollbackée selon la configuration."
              : "L'intégration APRIL est terminée.",
            'success'
          );
          this.loadHistory(0);
        },
        error: httpError => {
          this.error.set(httpError?.error?.detail ?? "L'intégration APRIL a échoué.");
          this.snackbarService.open("L'intégration APRIL a échoué.", 'error');
        }
      });
  }

  downloadRejectionReport(executionId: number): void {
    this.downloadReport(
      executionId,
      this.apiService.downloadAprilAdiRejectionReport(executionId),
      `rejets-april-execution-${executionId}.xlsx`,
      'Le téléchargement du rapport de rejets a échoué.'
    );
  }

  downloadIntegrationReport(executionId: number): void {
    this.downloadReport(
      executionId,
      this.apiService.downloadAprilAdiIntegrationReport(executionId),
      `integrations-april-execution-${executionId}.xlsx`,
      'Le téléchargement du rapport des intégrations a échoué.'
    );
  }

  private downloadReport(
    executionId: number,
    download$: Observable<Blob>,
    fileName: string,
    errorMessage: string
  ): void {
    if (this.downloadingReportId() !== null) {
      return;
    }

    this.downloadingReportId.set(executionId);

    download$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.downloadingReportId.set(null))
      )
      .subscribe({
        next: blob => this.saveBlob(blob, fileName),
        error: () => {
          this.snackbarService.open(errorMessage, 'error');
        }
      });
  }

  loadHistory(pageIndex: number): void {
    this.loadingHistory.set(true);

    this.apiService
      .getAprilAdiExecutionsPage(pageIndex, AprilAdiIntegrationPageComponent.HISTORY_PAGE_SIZE)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingHistory.set(false))
      )
      .subscribe({
        next: page => {
          this.historyItems.set(page.items);
          this.historyPageIndex.set(page.pageIndex);
          this.historyTotalCount.set(page.totalCount);
        },
        error: () => {
          this.snackbarService.open("La récupération de l'historique a échoué.", 'error');
        }
      });
  }

  executionStatusLabel(item: AprilAdiExecutionItem): string {
    if (item.simulation) {
      return 'Simulation';
    }

    return item.rolledBack ? 'Rollback' : 'Intégré';
  }

  private fileKey(file: File | null): string | null {
    return file ? `${file.name}|${file.size}|${file.lastModified}` : null;
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
